import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function LogoImg({ className, ...props }) {
  const [src, setSrc] = useState("/assets/sgm-logo.svg");
  const [retried, setRetried] = useState(false);
  return (
    <img
      src={src}
      alt="SGM Impianti"
      className={className}
      onError={() => {
        if (!retried) { setRetried(true); setSrc(`/assets/sgm-logo.svg?t=${Date.now()}`); }
      }}
      {...props}
    />
  );
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const utente = await login(email, password);
      toast.success("Accesso effettuato");
      // Lo schermo del capannone va sempre alla sua pagina; gli altri tornano
      // dove stavano andando prima che l'app chiedesse l'accesso.
      nav(utente?.role === "monitor" ? "/monitor" : (location.state?.da || "/"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Credenziali non valide");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <div className="hidden md:flex flex-1 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-cover bg-center animate-hero-fade-1" style={{ backgroundImage: "url(/assets/hero1.jpg)" }} />
          <div className="absolute inset-0 bg-cover bg-center animate-hero-fade-2" style={{ backgroundImage: "url(/assets/hero2.jpg)" }} />
          <div className="absolute inset-0 bg-slate-900/70" />
        </div>
        <div className="absolute top-0 left-0 right-0 h-3 bg-warning-stripe z-20" />
        <div className="absolute bottom-0 left-0 right-0 h-3 bg-warning-stripe z-20" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="flex justify-center">
            <LogoImg className="h-20 w-auto bg-white p-2 rounded" />
          </div>
          <div>
            <h1 className="font-display text-5xl font-black tracking-tighter leading-[0.95]">
              Gestione totale.<br/>
              <span className="text-orange-500">Dal cantiere alla sede.</span>
            </h1>
            <p className="mt-6 text-slate-300 max-w-md leading-relaxed">
              Personale, mezzi, cantieri, sicurezza, magazzino DPI e pianificazione:
              un solo sistema per tutta SGM Impianti.
            </p>
            <div className="mt-8 flex items-center gap-2 text-sm text-slate-300">
              <ShieldCheck className="w-5 h-5 text-orange-500" />
              <span>Localizzazione mezzi · Sicurezza in cantiere · Pianificazione squadre · Firma digitale</span>
            </div>
          </div>
          <div className="text-xs text-slate-400 text-center">v1.0 · © SGM Impianti srl</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 shadow-sm">
          <CardContent className="p-8">
            <div className="flex justify-center mb-8">
              <LogoImg className="h-16 w-auto" />
            </div>
            <div className="mb-8 text-center">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Accesso operatori</div>
              <h2 className="font-display text-3xl font-bold tracking-tight">Entra nel sistema</h2>
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-wider">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)}
                       className="h-12 mt-1" required data-testid="login-email" />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-wider">Password</Label>
                <Input id="password" type="password" value={password} onChange={(e)=>setPassword(e.target.value)}
                       className="h-12 mt-1" required data-testid="login-password" />
              </div>
              <Button type="submit" disabled={loading}
                      className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold tracking-wide"
                      data-testid="login-submit">
                {loading ? "Accesso in corso..." : "ACCEDI"}
              </Button>
            </form>
            {/* Chi viene a visitare un cantiere non ha un account: ha un
                codice, che gli da' il preposto sul posto. */}
            <a href="/visita"
               data-testid="vai-visita"
               className="block text-center text-xs text-slate-500 hover:text-[#0067b2] mt-5">
              Sono qui per visitare un cantiere e ho un codice
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
